import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

type CardStyledProps = {
  shadow?: boolean;
};

const CardStyled = styled.div<CardStyledProps>`
  background-color: #fff;
  box-shadow: ${({ theme, shadow = false }) => (shadow ? 'none' : theme.SHADOWS.PRIMARY)};
  margin-bottom: 0.8rem;
  text-align: left;
  width: 100%;
`;

const CardBodyStyled = styled.div`
  color: ${({ theme }) => theme.COLORS.PRIMARY};
  font-size: 1.4rem;
  line-height: 1.8;
  padding: 2.4rem 1.6rem;
  text-align: left;
  width: 100%;

  & ul {
    margin-left: 2.4rem;
  }

  @media ${({ theme }) => theme.DEVICE.LG} {
    padding: 4.8rem 5.6rem;
    font-size: 1.6rem;
  }
`;

const CardHeaderStyled = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.COLORS.ASH};
  cursor: pointer;
  user-select: none;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.COLORS.PRIMARY};
  font-weight: 500;
  display: flex;
  padding: 2.4rem 8rem 2.4rem 3.6rem;
  align-items: center;
  position: relative;

  @media ${({ theme }) => theme.DEVICE.LG} {
    font-size: 1.8rem;
    padding: 3.2rem 1.8rem 3.2rem 8rem;
  }
`;

type CardArrowProps = {
  children: ReactNode;
  rotate: boolean;
  value?: number;
  positionValue?: string;
  topPosition?: number | undefined;
};

type CardCheckProps = {
  children: ReactNode;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CardArrowStyled = styled(({ rotate, ...props }: CardArrowProps) => <span {...props} />)`
  position: absolute;
  left: 0;
  transition: transform 0.6s ease;
  ${({ topPosition = 0 }) =>
    topPosition &&
    css`
      top: ${topPosition}px;
    `}

  ${({ rotate, value = 90 }) =>
    rotate &&
    css`
      transform: translate(0, -4px) rotate(-${value}deg);
      transform-origin: center center;
    `}

  ${({ theme }) => theme.MEDIA.MD} {
    & img {
      margin: 0 1.2rem;
      height: 0.8rem;
    }

    left: ${({ positionValue }) => positionValue || '2.4rem'};
    ${({ topPosition = 0 }) =>
      topPosition &&
      css`
        top: ${topPosition * 3}px;
      `}
  }

  ${({ theme }) => theme.MEDIA.XXL} {
    ${({ topPosition = 0 }) =>
      topPosition &&
      css`
        top: ${topPosition * 4}px;
      `}
  }
`;

const CardCheckStyled = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: 4rem 4rem;
  justify-items: center;
  position: absolute;
  right: 0.4rem;

  @media ${({ theme }) => theme.DEVICE.LG} {
    grid-template-columns: 10rem 10rem;
    right: 1.6rem;
  }
`;

const CardTitleStyled = styled.span`
  width: calc(100% - 1.6rem);

  @media ${({ theme }) => theme.DEVICE.LG} {
    margin-left: 1.6rem;
    width: calc(100% - 6.4rem);
  }
`;

type AccordionCardProps = {
  children: ReactNode;
  shadow?: boolean;
};

function AccordionCard({ children, ...props }: AccordionCardProps): JSX.Element {
  return <CardStyled {...props}>{children}</CardStyled>;
}

function CardHeader({ children, ...props }: AccordionCardProps): JSX.Element {
  return <CardHeaderStyled {...props}>{children}</CardHeaderStyled>;
}

type AccordionCardBodyProps = {
  children: string;
};

function CardBody({ children, ...props }: AccordionCardBodyProps): JSX.Element {
  return <CardBodyStyled {...props} dangerouslySetInnerHTML={{ __html: children }} />;
}

function CardSpan({ children, ...props }: AccordionCardProps): JSX.Element {
  return <span {...props}>{children}</span>;
}

function CardArrow({ children, ...props }: CardArrowProps): JSX.Element {
  return <CardArrowStyled {...props}>{children}</CardArrowStyled>;
}

function CardCheck({ children }: CardCheckProps): JSX.Element {
  return <CardCheckStyled>{children}</CardCheckStyled>;
}

type AccordionCardTitleProps = {
  children: string;
};

function CardTitle({ children, ...props }: AccordionCardTitleProps): JSX.Element {
  return <CardTitleStyled {...props} dangerouslySetInnerHTML={{ __html: children }} />;
}

AccordionCard.Header = CardHeader;
AccordionCard.Body = CardBody;
AccordionCard.Span = CardSpan;
AccordionCard.Arrow = CardArrow;
AccordionCard.Check = CardCheck;
AccordionCard.Title = CardTitle;

export default AccordionCard;
